import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RentalUnit, CreateRentalUnitData, RentalUnitFilters } from '../../types';
import { rentalUnitService } from '../../services/rentalUnitService';
import type { RootState } from '../index';

// Type for limited rental unit data (for selection UI)
export interface LimitedRentalUnit {
  _id: string;
  floor: number;
  unitNumber: string;
  status: string;
}
interface RentalUnitsState {
  rentalUnits: RentalUnit[];
  currentRentalUnit: RentalUnit | null;
  rentalUnitsByProperty: { [propertyId: string]: RentalUnit[] };
  limitedUnitsByProperty: { [propertyId: string]: LimitedRentalUnit[] };  // Cached limited units
  tenantsByUnit: { [unitId: string]: any[] };
  analytics: {
    totalViews: number;
    totalApplications: number;
    averageStayDuration: number;
    revenueGenerated: number;
    occupancyRate: number;
  } | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters: RentalUnitFilters;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isUploadingImages: boolean;
  error: string | null;
}

const initialState: RentalUnitsState = {
  rentalUnits: [],
  currentRentalUnit: null,
  rentalUnitsByProperty: {},
  limitedUnitsByProperty: {},
  tenantsByUnit: {},
  analytics: null,
  pagination: {
    page: 1,
    limit: 16,
    total: 0,
    pages: 0
  },
  filters: {
    page: 1,
    limit: 16,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isUploadingImages: false,
  error: null
};

// Async thunks
export const fetchRentalUnits = createAsyncThunk(
  'rentalUnits/fetchRentalUnits',
  async (filters?: RentalUnitFilters) => {
    const response = await rentalUnitService.getRentalUnits(filters);
    return response;
  }
);

export const fetchRentalUnit = createAsyncThunk(
  'rentalUnits/fetchRentalUnit',
  async (id: string) => {
    const response = await rentalUnitService.getRentalUnit(id);
    return response.data;
  }
);

export const createRentalUnit = createAsyncThunk(
  'rentalUnits/createRentalUnit',
  async (data: CreateRentalUnitData) => {
    const response = await rentalUnitService.createRentalUnit(data);
    return response.data;
  }
);

export const bulkImportRentalUnits = createAsyncThunk(
  'rentalUnits/bulkImportRentalUnits',
  async (rentalUnits: CreateRentalUnitData[], { rejectWithValue }) => {
    try {
      const response = await rentalUnitService.bulkImportRentalUnits(rentalUnits);
      // Return the full response object which includes count, message, partial, errors
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to bulk import rental units');
    }
  }
);

export const updateRentalUnit = createAsyncThunk(
  'rentalUnits/updateRentalUnit',
  async ({ id, data }: { id: string; data: Partial<CreateRentalUnitData> }) => {
    const response = await rentalUnitService.updateRentalUnit(id, data);
    return response.data;
  }
);

export const deleteRentalUnit = createAsyncThunk(
  'rentalUnits/deleteRentalUnit',
  async (id: string) => {
    await rentalUnitService.deleteRentalUnit(id);
    return id;
  }
);

export const searchRentalUnits = createAsyncThunk(
  'rentalUnits/searchRentalUnits',
  async ({ query, filters }: { query: string; filters?: RentalUnitFilters }) => {
    const response = await rentalUnitService.searchRentalUnits(query, filters);
    return response;
  }
);

export const searchRentalUnitsWithPagination = createAsyncThunk(
  'rentalUnits/searchRentalUnitsWithPagination',
  async ({ query, filters, append = false }: {
    query: string;
    filters?: RentalUnitFilters;
    append?: boolean;
  }) => {
    const response = await rentalUnitService.searchRentalUnits(query, filters);
    return { ...response, append };
  }
);

export const fetchRentalUnitsByProperty = createAsyncThunk(
  'rentalUnits/fetchRentalUnitsByProperty',
  async ({ propertyId, filters }: {
    propertyId: string;
    filters?: { status?: string; isAvailable?: boolean }
  }) => {
    const response = await rentalUnitService.getRentalUnitsByProperty(propertyId, filters);
    return { propertyId, units: response.data };
  }
);

// Fetch limited rental units by property with caching
export const fetchLimitedRentalUnitsByProperty = createAsyncThunk(
  'rentalUnits/fetchLimitedRentalUnitsByProperty',
  async (propertyId: string, { getState }) => {
    // Check cache first
    const state = getState() as RootState;
    if (state.rentalUnits.limitedUnitsByProperty[propertyId]) {
      // Return cached data
      return { propertyId, units: state.rentalUnits.limitedUnitsByProperty[propertyId], fromCache: true };
    }
    // Fetch from API if not cached
    const response = await rentalUnitService.getLimitedRentalUnitsByProperty(propertyId);
    return { propertyId, units: response.data, fromCache: false };
  }
);

export const fetchRentalUnitTenants = createAsyncThunk(
  'rentalUnits/fetchRentalUnitTenants',
  async ({unitId, type = 'current'}: {unitId: string; type: 'current' | 'previous'}) => {
    const response = await rentalUnitService.getRentalUnitTenants(unitId, type);
    return { unitId, tenants: response.data };
  }
);

export const addTenantToUnit = createAsyncThunk(
  'rentalUnits/addTenantToUnit',
  async ({ unitId, data }: {
    unitId: string;
    data: {
      userId: string;
      tenancyType: string;
      moveInDate: string;
      rentAmount: number;
      securityDeposit: number;
    }
  }) => {
    await rentalUnitService.addTenantToUnit(unitId, data);
    return unitId;
  }
);

export const removeTenantFromUnit = createAsyncThunk(
  'rentalUnits/removeTenantFromUnit',
  async ({ unitId, tenantId }: { unitId: string; tenantId: string }) => {
    await rentalUnitService.removeTenantFromUnit(unitId, tenantId);
    return { unitId, tenantId };
  }
);

export const fetchRentalUnitAnalytics = createAsyncThunk(
  'rentalUnits/fetchRentalUnitAnalytics',
  async (unitId: string) => {
    const response = await rentalUnitService.getRentalUnitAnalytics(unitId);
    return response.data;
  }
);

export const toggleRentalUnitAvailability = createAsyncThunk(
  'rentalUnits/toggleRentalUnitAvailability',
  async ({ unitId, isAvailable }: { unitId: string; isAvailable: boolean }) => {
    const response = await rentalUnitService.toggleAvailability(unitId, isAvailable);
    return response.data;
  }
);

export const uploadRentalUnitImages = createAsyncThunk(
  'rentalUnits/uploadRentalUnitImages',
  async ({ unitId, files }: { unitId: string; files: File[] }) => {
    const response = await rentalUnitService.uploadImages(unitId, files);
    const images = (response.data?.images || []).map((img: any) => ({
      ...img,
      type: img.category || img.type || 'other', // Map category to type
      uploadedAt: img.uploadedAt || new Date().toISOString()
    }));
    return { unitId, images };
  }
);

export const deleteRentalUnitImage = createAsyncThunk(
  'rentalUnits/deleteRentalUnitImage',
  async ({ unitId, imageId }: { unitId: string; imageId: string }) => {
    await rentalUnitService.deleteImage(unitId, imageId);
    return { unitId, imageId };
  }
);

export const verifyRentalUnit = createAsyncThunk(
  'rentalUnits/verifyRentalUnit',
  async (unitId: string) => {
    const response = await rentalUnitService.verifyRentalUnit(unitId);
    return response.data;
  }
);

const rentalUnitsSlice = createSlice({
  name: 'rentalUnits',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<RentalUnitFilters>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        // Ensure limit is never undefined or 0
        limit: action.payload.limit || state.filters.limit || 8
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetRentalUnits: (state) => {
      state.rentalUnits = [];
      state.currentRentalUnit = null;
      state.rentalUnitsByProperty = {};
      state.limitedUnitsByProperty = {};
      state.tenantsByUnit = {};
      state.analytics = null;
      state.pagination = initialState.pagination;
    },
    setCurrentRentalUnit: (state, action: PayloadAction<RentalUnit | null>) => {
      state.currentRentalUnit = action.payload;
    },
    clearLimitedUnitsCache: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        delete state.limitedUnitsByProperty[action.payload];
      } else {
        state.limitedUnitsByProperty = {};
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch rental units
    builder
      .addCase(fetchRentalUnits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRentalUnits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rentalUnits = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.totalCount,
            pages: action.payload.pagination.totalPages
          };
        }
      })
      .addCase(fetchRentalUnits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch rental units';
      })
      
      // Fetch single rental unit
      .addCase(fetchRentalUnit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRentalUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.currentRentalUnit = action.payload;
        }
      })
      .addCase(fetchRentalUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch rental unit';
      })
      
      // Create rental unit
      .addCase(createRentalUnit.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createRentalUnit.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.rentalUnits.unshift(action.payload);
        }
      })
      .addCase(createRentalUnit.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create rental unit';
      })
      
      // Update rental unit
      .addCase(updateRentalUnit.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateRentalUnit.fulfilled, (state, action) => {
        state.isUpdating = false;
        const unit = action.payload;
        if (unit) {
          const index = state.rentalUnits.findIndex(u => u._id === unit._id);
          if (index !== -1) {
            state.rentalUnits[index] = unit;
          }
          if (state.currentRentalUnit?._id === unit._id) {
            state.currentRentalUnit = unit;
          }
        }
      })
      .addCase(updateRentalUnit.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to update rental unit';
      })
      
      // Delete rental unit
      .addCase(deleteRentalUnit.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteRentalUnit.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.rentalUnits = state.rentalUnits.filter(u => u._id !== action.payload);
        if (state.currentRentalUnit?._id === action.payload) {
          state.currentRentalUnit = null;
        }
      })
      .addCase(deleteRentalUnit.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.error.message || 'Failed to delete rental unit';
      })
      
      // Search rental units
      .addCase(searchRentalUnits.fulfilled, (state, action) => {
        state.rentalUnits = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.totalCount,
            pages: action.payload.pagination.totalPages
          };
        }
      })
      .addCase(searchRentalUnits.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to search rental units';
      })

      // Search rental units with pagination
      .addCase(searchRentalUnitsWithPagination.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchRentalUnitsWithPagination.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.append) {
          // Append to existing results (infinite scroll)
          state.rentalUnits = [...state.rentalUnits, ...action.payload.data];
        } else {
          // Replace results (new search)
          state.rentalUnits = action.payload.data;
        }
        if (action.payload.pagination) {
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.totalCount,
            pages: action.payload.pagination.totalPages
          };
        }
      })
      .addCase(searchRentalUnitsWithPagination.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to search rental units';
      })
      
      // Fetch rental units by property
      .addCase(fetchRentalUnitsByProperty.fulfilled, (state, action) => {
        if (action.payload?.units) {
          state.rentalUnitsByProperty[action.payload.propertyId] = action.payload.units;
        }
      })
      .addCase(fetchRentalUnitsByProperty.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch rental units for property';
      })
      
      // Fetch limited rental units by property (cached)
      .addCase(fetchLimitedRentalUnitsByProperty.fulfilled, (state, action) => {
        // Only update cache if not from cache (avoid unnecessary state updates)
        if (!action.payload.fromCache && action.payload?.units) {
          state.limitedUnitsByProperty[action.payload.propertyId] = action.payload.units;
        }
      })
      .addCase(fetchLimitedRentalUnitsByProperty.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch limited rental units for property';
      })
      
      // Fetch rental unit tenants
      .addCase(fetchRentalUnitTenants.fulfilled, (state, action) => {
        if (action.payload?.tenants) {
          state.tenantsByUnit[action.payload.unitId] = action.payload.tenants;
        }
      })
      .addCase(fetchRentalUnitTenants.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch tenants for rental unit';
      })
      
      // Add tenant to unit
      .addCase(addTenantToUnit.fulfilled, (state, action) => {
        // Refresh tenants for this unit
        const unitId = action.payload;
        if (state.tenantsByUnit[unitId]) {
          // Will be updated by fetchRentalUnitTenants
        }
      })
      .addCase(addTenantToUnit.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add tenant to unit';
      })
      
      // Remove tenant from unit
      .addCase(removeTenantFromUnit.fulfilled, (state, action) => {
        const { unitId, tenantId } = action.payload;
        if (state.tenantsByUnit[unitId]) {
          state.tenantsByUnit[unitId] = state.tenantsByUnit[unitId].filter(t => t._id !== tenantId);
        }
      })
      .addCase(removeTenantFromUnit.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove tenant from unit';
      })
      
      // Fetch analytics
      .addCase(fetchRentalUnitAnalytics.fulfilled, (state, action) => {
        if (action.payload) {
          state.analytics = action.payload;
        }
      })
      .addCase(fetchRentalUnitAnalytics.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch rental unit analytics';
      })
      
      // Toggle availability
      .addCase(toggleRentalUnitAvailability.fulfilled, (state, action) => {
        const unit = action.payload;
        if (unit) {
          const index = state.rentalUnits.findIndex(u => u._id === unit._id);
          if (index !== -1) {
            state.rentalUnits[index] = unit;
          }
          if (state.currentRentalUnit?._id === unit._id) {
            state.currentRentalUnit = unit;
          }
        }
      })
      .addCase(toggleRentalUnitAvailability.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to toggle rental unit availability';
      })
      
      // Upload images
      .addCase(uploadRentalUnitImages.pending, (state) => {
        state.isUploadingImages = true;
        state.error = null;
      })
      .addCase(uploadRentalUnitImages.fulfilled, (state, action) => {
        state.isUploadingImages = false;
        const { unitId, images } = action.payload;
        const unit = state.rentalUnits.find(u => u._id === unitId);
        if (unit) {
          unit.images = [...unit.images, ...images];
        }
        if (state.currentRentalUnit?._id === unitId) {
          state.currentRentalUnit.images = [...state.currentRentalUnit.images, ...images];
        }
      })
      .addCase(uploadRentalUnitImages.rejected, (state, action) => {
        state.isUploadingImages = false;
        state.error = action.error.message || 'Failed to upload images';
      })
      
      // Delete image
      .addCase(deleteRentalUnitImage.fulfilled, (state, action) => {
        const { unitId, imageId } = action.payload;
        const unit = state.rentalUnits.find(u => u._id === unitId);
        if (unit) {
          unit.images = unit.images.filter(img => img.url !== imageId);
        }
        if (state.currentRentalUnit?._id === unitId) {
          state.currentRentalUnit.images = state.currentRentalUnit.images.filter(img => img.url !== imageId);
        }
      })
      .addCase(deleteRentalUnitImage.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete image';
      })
      
      // Verify rental unit
      .addCase(verifyRentalUnit.fulfilled, (state, action) => {
        const unit = action.payload;
        if (unit) {
          const index = state.rentalUnits.findIndex(u => u._id === unit._id);
          if (index !== -1) {
            state.rentalUnits[index] = unit;
          }
          if (state.currentRentalUnit?._id === unit._id) {
            state.currentRentalUnit = unit;
          }
        }
      })
      .addCase(verifyRentalUnit.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to verify rental unit';
      });
  }
});

export const {
  setFilters,
  clearError,
  resetRentalUnits,
  setCurrentRentalUnit,
  clearLimitedUnitsCache
} = rentalUnitsSlice.actions;

export default rentalUnitsSlice.reducer;